import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

export interface AiProcessingGateResult {
  aiEnabled: boolean;
  isOwner: boolean;
  ensureAiOrPrompt: () => Promise<boolean>;
  sheetOpen: boolean;
  onEnableAi: () => Promise<void>;
  onCancelSheet: () => void;
  enabling: boolean;
}

export function useAiProcessingGate(spaceId: string | null): AiProcessingGateResult {
  const { user } = useAuth();
  const [aiEnabled, setAiEnabled] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  useEffect(() => {
    if (!spaceId) return;
    supabase
      .from("spaces")
      .select("ai_processing_enabled, owner_id")
      .eq("id", spaceId)
      .single()
      .then(({ data }) => {
        if (!data) return;
        const row = data as { ai_processing_enabled: boolean; owner_id: string };
        setAiEnabled(row.ai_processing_enabled);
        setIsOwner(!!user && row.owner_id === user.id);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId, user?.id]);

  const ensureAiOrPrompt = useCallback((): Promise<boolean> => {
    if (aiEnabled) return Promise.resolve(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setSheetOpen(true);
    });
  }, [aiEnabled]);

  const onEnableAi = useCallback(async () => {
    if (!spaceId || !isOwner) return;
    setEnabling(true);
    const { error } = await supabase
      .from("spaces")
      .update({ ai_processing_enabled: true })
      .eq("id", spaceId);
    setEnabling(false);
    if (!error) {
      setAiEnabled(true);
      setSheetOpen(false);
      resolverRef.current?.(true);
      resolverRef.current = null;
    }
    // On error: keep sheet open so user can retry or cancel
  }, [spaceId, isOwner]);

  const onCancelSheet = useCallback(() => {
    setSheetOpen(false);
    resolverRef.current?.(false);
    resolverRef.current = null;
  }, []);

  return { aiEnabled, isOwner, ensureAiOrPrompt, sheetOpen, onEnableAi, onCancelSheet, enabling };
}
